import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Card, CardContent, CardHeader } from '../../../components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../components/ui/collapsible';
import MedicalNotesDisplay from './MedicalNotesDisplay';
import coverageApiService from '../../../services/coverage-api.service';
import { sendToKinesis } from '../../../analytics/kinesisTransport';
import { useSelector } from 'react-redux';

/**
 * MedicalNotesTab - Displays medical coverage notes organized by indication
 */
const MedicalNotesTab = ({ indications, productId, formularyId, cachedNotes, onNotesUpdate }) => {
  const [expandedIndications, setExpandedIndications] = useState(new Set());
  
  // Get user and search context for analytics
  const userAttributes = useSelector((state) => state.auth?.userAttributes || {});
  const searchParams = useSelector((state) => state.coverage?.searchParams || {});

  const handleIndicationToggle = async (indicationId) => {
    const isExpanding = !expandedIndications.has(indicationId);
    
    // Toggle expansion state
    setExpandedIndications((prev) => {
      const newSet = new Set(prev);
      if (isExpanding) {
        newSet.add(indicationId);
      } else {
        newSet.delete(indicationId);
      }
      return newSet;
    });
    
    // Track medical notes expansion
    if (isExpanding) {
      try {
        const indication = indications.find(ind => ind.IndicationId === indicationId);
        await sendToKinesis({
          eventtype: 'MEDICAL_COVERAGE_INDICATION_VIEWED',
          userid: userAttributes.sub || userAttributes['cognito:username'] || 'anonymous',
          useremail: userAttributes.email || 'unknown',
          planId: searchParams.selectedPlan?.planid || '',
          planName: searchParams.selectedPlan?.planname || '',
          payer: searchParams.selectedPlan?.payer || '',
          pbm: searchParams.selectedPlan?.pbm || '',
          drugId: searchParams.selectedDrug?.drugid || '',
          drugName: searchParams.selectedDrug?.drugname || '',
          indicationId: indicationId || '',
          indicationName: indication?.Name || '',
          hasContent: !!cachedNotes[indicationId]?.data,
          timestamp: new Date().toISOString()
        });
      } catch (analyticsError) {
        console.error('Analytics error (non-blocking):', analyticsError);
      }
    }

    // If expanding and data not already fetched, fetch it
    if (isExpanding && !cachedNotes[indicationId]) {
      console.log('[Cache] Cache miss - fetching medical notes for indication:', indicationId);
      // Set loading state
      onNotesUpdate(indicationId, { data: null, loading: true, error: null });

      try {
        const response = await coverageApiService.getMedicalNotesByProduct(
          productId,
          formularyId,
          indicationId
        );

        if (response && response.result) {
          onNotesUpdate(indicationId, {
            data: response,
            loading: false,
            error: null,
          });
          
          // Track MMIT API call for medical notes
          try {
            await sendToKinesis('coverage_events', 'MMIT_API_CALLS_MADE', {
              userid: userAttributes?.sub || 'anonymous',
              organizationid: userAttributes?.['custom:org_owner_sub'] || userAttributes?.sub || 'anonymous',
              practice_id: userAttributes?.['custom:organization_id'] || '',
              session_id: window.currentSessionId || '',
              feature_module: 'medical_notes',
              mmit_api_call_count: 1,
              indicationId: indicationId?.toString() || '',
              planId: searchParams?.selectedPlan?.planid?.toString() || '',
              drugId: searchParams?.selectedDrug?.drugid?.toString() || '',
              timestamp: new Date().toISOString()
            });
          } catch (analyticsError) {
            console.error('Analytics error (non-blocking):', analyticsError);
          }
        } else {
          onNotesUpdate(indicationId, {
            data: null,
            loading: false,
            error: 'Failed to fetch medical notes',
          });
        }
      } catch (error) {
        console.error('Error fetching medical notes:', error);
        onNotesUpdate(indicationId, {
          data: null,
          loading: false,
          error: 'An error occurred while fetching medical notes',
        });
      }
    } else if (isExpanding && cachedNotes[indicationId]) {
      console.log('[Cache] Cache hit - using cached medical notes for indication:', indicationId);
    }
  };

  if (!indications || indications.length === 0) {
    return (
      <div className="p-12 text-center space-y-4">
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto">
          <svg className="h-8 w-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 16v-4m0-4h.01" />
          </svg>
        </div>
        <div className="space-y-2">
          <h3 className="text-base font-semibold text-foreground">
            Medical Notes Not Provided
          </h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            No indication information is listed for medical coverage notes. This drug may only have pharmacy benefit coverage.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {indications.map((indication) => {
            const isExpanded = expandedIndications.has(indication.IndicationId);
            const cache = cachedNotes[indication.IndicationId];

            return (
              <Collapsible
                key={indication.IndicationId}
                open={isExpanded}
                onOpenChange={() => handleIndicationToggle(indication.IndicationId)}
              >
                <Card className="border-2 shadow-sm">
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <span className="text-base font-medium text-foreground">{indication.Name}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {cache?.loading && (
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          )}
                          {isExpanded ? (
                            <ChevronUp className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <CardContent className="pt-0 pb-4 px-4">
                    {cache?.loading ? (
                      <div className="flex flex-col items-center justify-center py-16 px-4 bg-muted/30 rounded-lg border-2 border-dashed">
                        <Loader2 className="h-10 w-10 text-primary animate-spin mb-4" />
                        <p className="text-sm font-medium text-foreground mb-1">
                          Loading Medical Notes for {indication.Name}...
                        </p>
                        <p className="text-xs font-normal text-muted-foreground text-center max-w-md">
                          Fetching medical coverage notes
                        </p>
                      </div>
                    ) : cache?.error ? (
                      <Alert variant="destructive">
                        <AlertDescription>{cache.error}</AlertDescription>
                      </Alert>
                    ) : cache?.data ? (
                      <MedicalNotesDisplay 
                        notes={cache.data.result.RuleNotes}
                        indicationName={indication.Name}
                      />
                    ) : null}
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            );
          })}
    </div>
  );
};

export default MedicalNotesTab;
